<template>
  <v-form ref="form">
    <div
      class="pa-0"
      style="
        background-color: var(--color-bg);
        display: flex;
        flex-direction: column;
        gap: 10px;
      "
    >
      <SpeedSettingsDisplay />
      <TitledNumericalInputField
        inputLabel="LHV (kJ/kg)"
        iconName="mdi-fire"
        :onChange="onLowerHeatingValueChanged"
        :value="+calculationData.lowerHeatingValue"
        :rules="[_validateLhv]"
      />
      <TitledNumericalInputField
        inputLabel="Mean draft (m)"
        iconName="mdi-waves"
        :onChange="onMeanDraftChanged"
        :value="+calculationData.meanDraft"
        :rules="[_validateDraft]"
      />
      <TitledNumericalInputField
        inputLabel="Trim (m)"
        iconName="mdi-angle-obtuse"
        :onChange="onTrimChanged"
        :value="+calculationData.trim"
        :rules="[_validateTrim]"
      />
      <TitledNumericalInputField
        inputLabel="True wind (bft)"
        iconName="mdi-weather-windy"
        :onChange="onTrueWindChanged"
        :value="+calculationData.trueWindSpeed"
        :rules="[_validateTrueWind]"
      />
      <TitledSelectionField
        inputLabel="Wind direction"
        iconName="mdi-windsock"
        :items="windDirectionOptions"
        :onChange="onWindDirectionChanged"
        :value="displayWindDirection"
      />
      <TitledNumericalInputField
        inputLabel="Fouling"
        iconName="mdi-grass"
        :onChange="onFoulingChanged"
        :value="+calculationData.fouling"
        tooltipText="Fouling is the amount of fouling on the hull of the ship."
      />

      <v-btn
        depressed
        rounded
        block
        class="use-latest-fouling-btn mt-1"
        :loading="fetchingLatestFouling"
        @click="onUseLatestFoulingClicked"
      >
        <v-icon small left>mdi-refresh</v-icon>
        Use Latest Fouling
      </v-btn>


      <DateRangePicker />
    </div>
  </v-form>
</template>

<script>
import { mapState } from "vuex";
import TitledNumericalInputField from "./TitledNumericalInputField.vue";
import TitledSelectionField from "./TitledSelectionField.vue";
import SpeedSettingsDisplay from "./SpeedSettingsDisplay.vue";
import DateRangePicker from "./DateRangePicker.vue";

export default {
  name: "InteractiveCalculationView",
  components: {
    TitledNumericalInputField,
    TitledSelectionField,
    SpeedSettingsDisplay,
    DateRangePicker,
  },
  props: {},
  data() {
    return {
      windDirectionOptions: ["Head Seas", "Beam Seas", "Following Seas"],
      fetchingLatestFouling: false,
    };
  },
  computed: {
    ...mapState("kDiagram", ["calculationData"]),

    displayWindDirection() {
      return this.calculationData.windDirection || "Head Seas";
    },
  },
  methods: {
    onSpeedThroughWaterChanged(value) {
      this.$store.commit("kDiagram/SET_SPEED_THROUGH_WATER", value);
    },
    onLowerHeatingValueChanged(value) {
      this.$store.commit("kDiagram/SET_LOWER_HEATING_VALUE", +value);
    },
    onMeanDraftChanged(value) {
      this.$store.commit("kDiagram/SET_MEAN_DRAFT", +value);
    },
    onTrimChanged(value) {
      this.$store.commit("kDiagram/SET_TRIM", +value);
    },
    onTrueWindChanged(value) {
      this.$store.commit("kDiagram/SET_TRUE_WIND_SPEED", +value);
    },
    onWindDirectionChanged(value) {
      this.$store.commit("kDiagram/SET_WIND_DIRECTION", value);
    },
    onFoulingChanged(value) {
      this.$store.commit("kDiagram/SET_FOULING", +value);
    },
    async onUseLatestFoulingClicked() {
      try {
        this.fetchingLatestFouling = true;
        await this.$store.dispatch(
          "kDiagram/fetchLatestFouling",
          this.$route.params.vessel
        );
      } catch (e) {
        console.error("Failed to fetch latest fouling", e);
      } finally {
        this.fetchingLatestFouling = false;
      }
    },
    _validateStw(values) {
      const regex = /^[0-9]+(\.[0-9]+)?(,[0-9]+(\.[0-9]+)?)*$/;
      return regex.test(values) || "Insert comma separated values ex: 9,9.5,10";
    },
    _validateLhv(value) {
      return (value > 40000 && value < 50000) || "LHV must be [40,50] MJ/kg";
    },
    _validateTrim(value) {
      return (value > -5 && value < 5) || "Trim must be [-5,5]";
    },
    _validateDraft(value) {
      return (value > 0 && value < 30) || "Draft must be in [0,30]";
    },
    _validateTrueWind(value) {
      return (value > 0 && value < 10) || "True wind must be in [0,10]";
    },
    validate() {
      return this.$refs.form.validate();
    },
  },
};
</script>

<style scoped>
.v-navigation-drawer {
  background: white !important;
}

.use-latest-fouling-btn {
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  font-weight: 600 !important;
  height: 30px !important;
  background: #0d0b30 !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(79, 70, 229, 0.35) !important;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
}
.use-latest-fouling-btn:hover {
  background: #6366f1 !important;
  box-shadow: 0 3px 12px rgba(99, 102, 241, 0.5) !important;
  transform: translateY(-1px);
}
.use-latest-fouling-btn:active {
  transform: translateY(0);
}

</style>
